import React, { useState } from 'react';
import styled from 'styled-components';
import { FaShieldAlt, FaCode, FaLinux, FaBug, FaNetworkWired, FaTools, FaMobileAlt, FaCloud, FaMicrochip, FaCog } from 'react-icons/fa';

// --- Styled Components ---

const AppContainer = styled.div`
  background-color: #f7f8fa;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: 'Inter', sans-serif;
  padding: 40px;
`;

const SyllabusLayout = styled.div`
  display: flex;
  width: 100%;
  max-width: 1200px;
  background: white;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid #e9ecef;
`;

const Sidebar = styled.div`
  flex: 0 0 220px;
  background: #ffffff;
  padding: 30px 15px;
  border-right: 1px solid #e9ecef;
`;

const SidebarTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #868e96;
  padding: 0 15px 15px 15px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 15px;
`;

const WeekButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  padding: 12px 15px;
  margin-bottom: 5px;
  border-radius: 8px;
  font-size: 15px;
  cursor: pointer;
  color: ${props => (props.active ? '#333' : '#495057')};
  background-color: ${props => (props.active ? '#f1f3f5' : 'transparent')};
  font-weight: ${props => (props.active ? '600' : '500')};
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding: 30px;
  background-color: #fcfdff;
  // A subtle background pattern can be added here if you have an image
  // background-image: url('/path-to-doodle-background.svg');
`;

const WeekTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #212529;
  margin-bottom: 25px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
`;

const SkillCard = styled.div`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  transition: box-shadow 0.3s ease, transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
`;

const CardTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardIcon = styled.div`
  font-size: 24px;
  color: ${props => props.color || '#4C6EF5'};
`;

const CardTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: #343a40;
  margin: 0;
`;

const CardDuration = styled.span`
  font-size: 13px;
  color: #868e96;
  white-space: nowrap;
  background: #f1f3f5;
  padding: 4px 8px;
  border-radius: 6px;
`;

const CardSubtitle = styled.h5`
  font-size: 15px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 8px;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: #495057;
  line-height: 1.6;
  margin-bottom: 15px;
`;

const CardFooter = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #0ca678; // A green accent for projects
  border-top: 1px solid #f1f3f5;
  padding-top: 12px;
  margin-top: 15px;
`;

const Disclaimer = styled.p`
  font-size: 12px;
  color: #adb5bd;
  margin-top: 30px;
  text-align: center;
`;

// --- Syllabus Data (Grouped from brochure) ---

const syllabusByWeek = {
  'Weeks 1-4: Foundations': [
    { 
      icon: <FaShieldAlt />, 
      color: '#4C6EF5',
      title: 'Cyber Security Fundamentals', 
      duration: '35 hours', 
      subtitle: 'Intro, Ethics, and Law', 
      description: 'Learn about the types of hackers [cite: 176][cite_start], hacking methodologies [cite: 178, 179][cite_start], penetration testing [cite: 180][cite_start], and the legal landscape including the Indian IT Act[cite: 182, 183].' 
    },
    { 
      icon: <FaCode />, 
      color: '#FD7E14',
      title: 'Web & Programming Basics', 
      duration: '40 hours', 
      subtitle: 'HTML, CSS, JS, PHP, SQL', 
description: 'Develop fundamental skills in web development, from front-end beautification with CSS [cite: 193] [cite_start]to back-end logic with PHP [cite: 197] [cite_start]and database management with SQL[cite: 200, 201].' 
    },
    { 
      icon: <FaLinux />, 
      color: '#20C997',
      title: 'Linux & Hacking Lab Setup', 
      duration: '25 hours', 
      subtitle: 'Kali, Parrot, and Commands', 
      description: 'Master the Linux OS, understand its file system [cite: 227][cite_start], and learn advanced commands[cite: 226]. [cite_start]Set up your own hacking lab using Kali Linux [cite: 243] [cite_start]and the Tor browser[cite: 252].' 
    },
  ],
  'Weeks 5-9: Web Exploitation': [
    { 
      icon: <FaBug />, 
      color: '#E64980',
      title: 'Web Vulnerability Analysis', 
      duration: '60 hours', 
      subtitle: 'OWASP Top 10 & VAPT', 
      description: 'Dive deep into the OWASP Top 10 vulnerabilities [cite: 269][cite_start], learning to exploit and defend against SQL Injection [cite: 270, 271] [cite_start]and Cross-Site Scripting (XSS)[cite: 275, 276]. [cite_start]Automate VAPT with tools like Nmap, Nikto, and Burp Suite Pro[cite: 317].' 
    },
    { 
      icon: <FaCog />, 
      color: '#845EF7',
      title: 'Bug Bounty Hunting', 
      duration: '55 hours', 
      subtitle: 'Recon, Exploitation & Reporting', 
      description: 'Learn the full bug bounty lifecycle, from reconnaissance [cite: 330] [cite_start]and advanced exploitation (SSRF, RCE) [cite: 334] [cite_start]to bypassing web application firewalls (WAF) [cite: 336] [cite_start]and writing impactful reports to maximize payouts[cite: 337].' 
    },
  ],
  'Weeks 10-15: Core Hacking (CEH Modules)': [
      {
          icon: <FaNetworkWired />,
          color: '#15AABF',
          title: 'Network Recon & Enumeration',
          duration: '70 hours',
          subtitle: 'Footprinting, Scanning, Enumeration',
          description: 'Master the art of information gathering through footprinting [cite: 372][cite_start], scanning networks with tools like Nmap [cite: 418][cite_start], and enumerating services like NetBIOS [cite: 435][cite_start], SNMP [cite: 436][cite_start], and DNS [cite: 438] to map out target infrastructure.'
      },
      {
          icon: <FaTools />,
          color: '#FCC419',
          title: 'System & Malware Hacking',
          duration: '65 hours',
          subtitle: 'Gaining Access & Persistence',
          description: 'Learn to gain access to systems, escalate privileges [cite: 475][cite_start], and maintain access using backdoors[cite: 493]. [cite_start]Analyze malware [cite: 512][cite_start], sniff network traffic with Wireshark [cite: 538][cite_start], and perform social engineering attacks[cite: 543].'
      }
  ],
  'Weeks 16-20: Advanced & Specialized Hacking': [
      {
          icon: <FaMobileAlt />,
          color: '#212529',
          title: 'Mobile, Wireless & IoT Hacking',
          duration: '60 hours',
          subtitle: 'Android, iOS, Wi-Fi & Smart Devices',
          description: 'Explore attack vectors for mobile platforms like Android and iOS[cite: 679, 680]. [cite_start]Learn to hack wireless networks [cite: 658] [cite_start]and discover vulnerabilities in the growing landscape of IoT and OT devices[cite: 697, 700].'
      },
      {
          icon: <FaCloud />,
          color: '#4C6EF5',
          title: 'Cloud & Cryptography',
          duration: '40 hours',
          subtitle: 'AWS, Azure & Encryption Attacks',
          description: 'Understand cloud computing threats [cite: 720] [cite_start]and learn to hack cloud platforms like AWS, Azure, and Google Cloud[cite: 722, 723, 724]. [cite_start]Dive into cryptography concepts and learn to perform cryptanalysis[cite: 740].'
      },
      {
          icon: <FaMicrochip />,
          color: '#FD7E14',
          title: 'Hardware & Drone Hacking',
          duration: '30 hours',
          subtitle: 'Raspberry Pi, Flipper Zero & Drones',
          description: 'Get hands-on with hardware hacking using Raspberry Pi and Flipper Zero for RFID/NFC cloning[cite: 768]. [cite_start]Learn to identify and exploit drone attack surfaces, including WiFi deauthentication and GPS spoofing[cite: 793, 814].'
      }
  ]
};

const weeks = Object.keys(syllabusByWeek);

function ModernSyllabus() {
  const [activeWeek, setActiveWeek] = useState(weeks[0]);

  return (
    <AppContainer>
      <SyllabusLayout>
        <Sidebar>
          <SidebarTitle>Detailed curriculum</SidebarTitle>
          {weeks.map(week => (
            <WeekButton 
              key={week} 
              active={activeWeek === week}
              onClick={() => setActiveWeek(week)}
            >
              {week}
            </WeekButton>
          ))}
        </Sidebar>
        <ContentArea>
          <WeekTitle>{activeWeek}</WeekTitle>
          <CardGrid>
            {syllabusByWeek[activeWeek].map(skill => (
              <SkillCard key={skill.title}>
                <CardHeader>
                  <CardTitleContainer>
                    <CardIcon color={skill.color}>{skill.icon}</CardIcon>
                    <CardTitle>{skill.title}</CardTitle>
                  </CardTitleContainer>
                  <CardDuration>{skill.duration}</CardDuration>
                </CardHeader>
                <CardSubtitle>{skill.subtitle}</CardSubtitle>
                <CardDescription>{skill.description}</CardDescription>
                [cite_start]<CardFooter>Apply on 10+ Real-time Hacking Projects [cite: 151]</CardFooter>
              </SkillCard>
            ))}
          </CardGrid>
          <Disclaimer>
            *The weekly plans can be re-structured as we keep enhancing learning designs basis ongoing recruiter and student feedback.
          </Disclaimer>
        </ContentArea>
      </SyllabusLayout>
    </AppContainer>
  );
}

export default ModernSyllabus;